/* © CNES */
import { Chip } from 'primereact/chip';
import { Dialog } from 'primereact/dialog';
import { Divider } from 'primereact/divider';
import { Image } from 'primereact/image';
import { RadioButton } from 'primereact/radiobutton';
import { ScrollPanel } from 'primereact/scrollpanel';
import { Sidebar } from 'primereact/sidebar';
import { useContext, useEffect, useMemo, useState } from 'react';
import { ConfigContext } from '../../../context/ConfigContext';
import placeholder_quicklook from '../../../placeholder_quicklook.png';
import { CustomAccordion } from '../../shared/Accordion/CustomAccordion';
import { Geotiff } from '../../shared/Geotiff/Geotiff';
import { LocationTree } from '../../shared/metadata/LocationTree';
import { ProductDetails } from '../../shared/metadata/ProductDetails';
import { replaceCatalogPathForQuicklook } from '../../shared/Utils';
import { CollectionActionButtons } from './CollectionActionButtons/CollectionActionButtons';
import { CollectionHeader } from './CollectionHeader/CollectionHeader';
import { ReactComponent as Preview } from './icons/preview.svg';
import './RightSidePanel.scss';

/**
 * RightSidePanelProps interface
 */
interface RightSidePanelProps {
  setDownloadProgress: Function;
  isLoading: { showSpinner: boolean; productId: string }[];
  setIsLoading: Function;
  isDatalakeAvailable: string;
  setMaxProductsWarningVisible: Function;
}

/**
 * Right Side Panel displayed on selection of a product from results.
 * @returns Element.
 */
export const RightSidePanel = ({
  setDownloadProgress,
  isLoading,
  setIsLoading,
  isDatalakeAvailable,
  setMaxProductsWarningVisible,
}: RightSidePanelProps) => {
  const context = useContext(ConfigContext);

  const showPanel = useMemo(() => {
    return Boolean(context.isRightSidePanelShown);
  }, [context.isRightSidePanelShown]);

  const [showRightPanel, setShowPanel] = useState(false);
  const [itemAvailability, setItemAvailability] = useState<boolean | undefined>(
    undefined
  );
  const [quicklookSD, setQuicklookSD] = useState<string | undefined>(undefined);
  const [miniature, setMiniature] = useState<string | undefined>(undefined);
  const [showQuicklookDialog, setShowQuicklookDialog] =
    useState<boolean>(false);
  const [pyramid, setPyramid] = useState<string>('Classification');
  const [quicklookBlob, setQuicklookBlob] = useState<Blob | undefined>(
    undefined
  );

  /**
   * fetch new tiff on pyramid change
   * @param pyramidType selected pyramid type to display
   */
  const onOpenQuicklookImage = (pyramidType: string) => {
    setPyramid(pyramidType);
    const imageURL = context.selectedProduct.pyramid.find((el) =>
      el.key.includes(pyramidType.toUpperCase())
    );
    if (imageURL) {
      fetch(replaceCatalogPathForQuicklook(imageURL.url))
        .then((response) => {
          return response.blob();
        })
        .then((blob) => {
          setQuicklookBlob(blob);
          setShowQuicklookDialog(true);
        })
        .catch((error) => {
          console.warn('error', error);
        });
    }
  };

  /**
   * set Show Panel on context change.
   */
  useEffect(() => {
    setShowPanel(context.isLeftSidePanelShown);
  }, [context]);

  /**
   * set Product ID on context selectedProduct change.
   */
  useEffect(() => {
    setItemAvailability(
      context.selectedProduct?.collectionDataModel?.general?.storageMode ===
        'Datalake Tier 2'
        ? true
        : false
    );
  }, [context.selectedProduct]);

  /**
   * Set up miniature and zoomed image depending on thumbnail
   * and quicklook fields from the viewed selected product.
   */
  useEffect(() => {
    if (context.selectedProduct?.quicklook) {
      const quicklook = context.selectedProduct?.quicklook;
      setQuicklookSD(replaceCatalogPathForQuicklook(quicklook));
      if (context.selectedProduct?.thumbnail) {
        const thumbnail = context.selectedProduct?.thumbnail;
        setMiniature(replaceCatalogPathForQuicklook(thumbnail));
      } else {
        setMiniature(replaceCatalogPathForQuicklook(quicklook));
      }
    } else {
      setQuicklookSD(undefined);
      setMiniature(undefined);
    }
  }, [context.selectedProduct.quicklook, context.selectedProduct.thumbnail]);

  const customHeader = (
    <div>
      <div className="quicklook" aria-label="quicklook">
        <Image
          src={miniature || placeholder_quicklook}
          {...(!context.selectedProduct?.pyramid?.length && {
            zoomSrc: quicklookSD,
          })}
          onError={(e) => {
            if ((e.target as HTMLImageElement).src !== placeholder_quicklook) {
              (e.target as HTMLImageElement).src = placeholder_quicklook;
            }
          }}
          alt="placeholder_quicklook"
          width="100%"
          height="100%"
          {...(!context.selectedProduct?.pyramid?.length && { preview: true })}
        />
        {!!context.selectedProduct?.pyramid?.length && (
          <div
            className="overlay"
            aria-label="overlay-quicklook"
            onClick={() => {
              onOpenQuicklookImage('Classification');
            }}
          >
            <Preview className="icon" />
          </div>
        )}
      </div>
      <h2 className="RightPanelTitle">
        {context.selectedProduct?.productName}
      </h2>
    </div>
  );

  return (
    <>
      <Dialog
        visible={showQuicklookDialog}
        modal={true}
        className="dialogQuicklook"
        onHide={() => setShowQuicklookDialog(false)}
        showHeader={true}
      >
        {context.selectedProduct?.pyramid?.length && (
          <>
            <div className="GeotiffMapContainer">
              <Geotiff blob={quicklookBlob} processName={''} />
            </div>
            <div className="flex flex-wrap gap-3 m-3 justify-content-center">
              <fieldset className="flex flex-wrap gap-3 m-3 p-3 justify-content-center">
                <legend>Choose the layer to display</legend>
                <div className="flex align-items-center">
                  <RadioButton
                    inputId="GeoTiff1"
                    name="pyramid"
                    value="Classification"
                    onChange={(e) => onOpenQuicklookImage(e.value)}
                    checked={pyramid === 'Classification'}
                  />
                  <label htmlFor="GeoTiff1" className="ml-2">
                    Classification
                  </label>
                </div>
                <div className="flex align-items-center">
                  <RadioButton
                    inputId="GeoTiff2"
                    name="pyramid"
                    value="Confidence"
                    onChange={(e) => onOpenQuicklookImage(e.value)}
                    checked={pyramid === 'Confidence'}
                  />
                  <label htmlFor="GeoTiff2" className="ml-2">
                    Confidence
                  </label>
                </div>
                <div className="flex align-items-center">
                  <RadioButton
                    inputId="GeoTiff3"
                    name="pyramid"
                    value="validity"
                    onChange={(e) => onOpenQuicklookImage(e.value)}
                    checked={pyramid === 'validity'}
                  />
                  <label htmlFor="GeoTiff3" className="ml-2">
                    Pixel Validity
                  </label>
                </div>
              </fieldset>
            </div>
          </>
        )}
        <div className="flex justify-content-center">
          <Image
            src={quicklookSD}
            onError={(e) => {
              if (
                (e.target as HTMLImageElement).src !== placeholder_quicklook
              ) {
                (e.target as HTMLImageElement).src = placeholder_quicklook;
              }
            }}
            alt="placeholder_quicklook"
            width="100%"
            height="100%"
          />
        </div>
      </Dialog>
      {showRightPanel && (
        <Sidebar
          icons={customHeader}
          showCloseIcon={true}
          modal={false}
          visible={showPanel}
          dismissable={false}
          position="right"
          onHide={() => context.setRightPanelVisibility(!showPanel)}
        >
          <ScrollPanel
            style={{
              width: '100%',
              height: '100%',
            }}
          >
            <div className="CollectionPanel">
              <CollectionActionButtons
                storageMode={itemAvailability}
                setDownloadProgress={setDownloadProgress}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
                isDatalakeAvailable={isDatalakeAvailable}
                setMaxProductsWarningVisible={setMaxProductsWarningVisible}
              />
              <Divider style={{ border: '1px solid #002d80' }} />
              <div className="CollectionDetails">
                <CollectionHeader
                  data={{
                    name: context.selectedProduct?.collectionDataModel?.general
                      .collection,
                    endDateTime:
                      context.selectedProduct?.collectionDataModel?.general
                        ?.sensingEnd,
                    startDateTime:
                      context.selectedProduct?.collectionDataModel?.general
                        ?.sensingStart,
                    storageMode:
                      context.selectedProduct?.collectionDataModel?.general
                        ?.storageMode,
                    totalSize:
                      context.selectedProduct.collectionDataModel?.general
                        .totalSize,
                  }}
                  isDatalakeAvailable={isDatalakeAvailable}
                />

                <div className="CollectionCategory">
                  <CustomAccordion
                    title={'Product details'}
                    key={0}
                    closed
                    children={
                      <ProductDetails
                        productDetails={
                          context?.selectedProduct?.collectionDataModel
                            ?.productDetails
                        }
                      />
                    }
                  />
                </div>

                <div className="CollectionCategory">
                  <CustomAccordion
                    title={'Geometry'}
                    key={2}
                    closed
                    children={
                      <div className="HeaderRow">
                        <p className="RowValue geometry">
                          {
                            context.selectedProduct?.collectionDataModel
                              ?.geometry?.multipolygon
                          }
                        </p>
                      </div>
                    }
                  />
                </div>

                <div className="CollectionCategory">
                  <CustomAccordion
                    title={'Keywords'}
                    key={3}
                    closed
                    children={
                      <>
                        <div className="ChipsContainer">
                          {context.selectedProduct?.collectionDataModel?.keywords?.map(
                            (element, index) => {
                              return (
                                <div key={element + index}>
                                  <Chip
                                    className="CustomChip"
                                    label={element}
                                  />
                                </div>
                              );
                            }
                          )}
                        </div>
                      </>
                    }
                  />
                </div>

                <div className="CollectionCategory">
                  <CustomAccordion
                    title={'Location'}
                    key={4}
                    closed
                    children={
                      <LocationTree
                        locationData={
                          context.selectedProduct?.collectionDataModel?.location
                        }
                      />
                    }
                  />
                </div>
              </div>
            </div>
          </ScrollPanel>
        </Sidebar>
      )}
    </>
  );
};
